/**
 * When a screen cannot load (the owner, 2026-10-12: "why there are so many pages throwing errors").
 *
 * Each deploy renames the screens' files (OnboardingScreen-B21MQcFu.js becomes OnboardingScreen-Drw4DhRF.js). A tab
 * opened before the deploy still asks for the old names, so every screen it had not opened yet failed with "Failed to
 * fetch dynamically imported module" and React Router's developer page. A missing screen file now reloads the desk
 * once, onto the new version; any other error shows a plain page with Reload and Home instead of the developer page.
 */
import { useRouteError } from 'react-router-dom';
import { newVersionIsReady } from '../lib/newVersion';

const KEY = 'rtcom.reloadedForNewVersion';

/** A screen's file could not be fetched or run: the desk was deployed again since this tab opened. */
export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Unable to preload CSS/i.test(text);
}

/** Reload once onto the new version. A second failure within 15 s is shown, not reloaded again (no loop). */
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 15_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* storage refused: reload anyway, once per call */ }
  window.location.reload();
  return true;
}

export function RouteError() {
  const error = useRouteError();
  // A tab that is behind runs old and new files together, which can fail in other words ("Cannot read properties of undefined (reading 'default')"):
  // when the tab already knows a newer version is live, any error that is not a plain response is treated as the stale tab's.
  const chunk = isChunkLoadError(error) || (newVersionIsReady() && error instanceof Error);
  const reloading = chunk && reloadForNewVersion();
  const message = error instanceof Error ? error.message : typeof error === 'object' && error && 'statusText' in error ? String((error as { statusText: unknown }).statusText) : String(error ?? '');
  return (
    <div style={{ maxWidth: 560, margin: '12vh auto', padding: 24, fontFamily: 'system-ui, sans-serif', color: '#1b1b1b' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>{chunk ? 'A new version of the desk is ready' : 'This page could not be shown'}</h1>
      <p style={{ margin: '0 0 16px', color: '#555' }}>
        {chunk
          ? reloading ? 'Loading it now…' : 'Reload the page to use it.'
          : 'Something went wrong while opening this page. Reloading usually fixes it; if it does not, send a screenshot of this message.'}
      </p>
      {!chunk && message ? <pre style={{ whiteSpace: 'pre-wrap', background: '#f2f2f2', padding: 12, borderRadius: 6, fontSize: 12 }}>{message}</pre> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => window.location.reload()} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #1d3a2b', background: '#1d3a2b', color: '#fff', cursor: 'pointer' }}>Reload</button>
        <button type="button" onClick={() => { window.location.href = '/'; }} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}>Home</button>
      </div>
    </div>
  );
}
